/**
 * Dashboard › FAQ (PRIVATE fork) — full CRUD over letpot-api /admin/faq via
 * @greenhouse/crud. Showcases the `richtext` extension field (S12 seam).
 */

import { useMemo } from 'react';
import { defineCrud, CrudPage } from '@greenhouse/crud';
import { HelpCircle } from '../../../lib/icons';
import { letpotDataSource } from '../../../lib/dashboard/data-source';

interface FaqRow {
  _id: string;
  code: string;
  type: 'error' | 'product' | 'common';
  title: string;
  question: string;
  tags: string[];
  content: string;
  read_count: number;
  updated_at: string;
}

const TYPE = [
  { value: 'error', label: 'Error code' },
  { value: 'product', label: 'Product' },
  { value: 'common', label: 'Common' },
];

const dataSource = letpotDataSource<FaqRow>('faq');

export function DashboardFaq() {
  const schema = useMemo(
    () =>
      defineCrud<FaqRow>({
        name: 'FAQ',
        icon: HelpCircle,
        idField: '_id',
        dataSource,
        defaultSort: { key: 'updated_at', order: 'desc' },
        columns: [
          { key: 'code', label: 'Code', sortable: true, width: '8rem' },
          { key: 'title', label: 'Title' },
          {
            key: 'type',
            label: 'Type',
            type: 'badge',
            width: '7rem',
            badgeMap: { error: 'destructive', product: 'secondary', common: 'default' },
          },
          { key: 'tags', label: 'Tags', type: 'tags', responsiveHide: 'md' },
          { key: 'read_count', label: 'Reads', type: 'number', align: 'right', sortable: true, responsiveHide: 'lg' },
          { key: 'updated_at', label: 'Updated', type: 'date', sortable: true, responsiveHide: 'md' },
          { key: 'question', label: 'Question', hidden: true },
          {
            key: 'content',
            label: 'Content',
            type: 'custom',
            hidden: true,
            render: (r) => <div className="prose-sm" dangerouslySetInnerHTML={{ __html: r.content ?? '' }} />,
          },
        ],
        filters: [
          { key: 'title', label: 'Search', kind: 'text' },
          { key: 'code', label: 'Code', kind: 'text', secondary: true },
          { key: 'type', label: 'Type', kind: 'select', options: TYPE },
        ],
        formFields: [
          { key: 'code', label: 'Code', type: 'text', required: true, width: 2, comment: 'Unique identifier' },
          { key: 'type', label: 'Type', type: 'select', options: TYPE, width: 2, defaultValue: 'common' },
          { key: 'title', label: 'Title', type: 'text', required: true },
          { key: 'question', label: 'Question', type: 'textarea', rows: 2 },
          { key: 'tags', label: 'Tags', type: 'tags' },
          { key: 'content', label: 'Content', type: 'extension', name: 'richtext', config: { minRows: 8 } },
        ],
        detailTabs: [{ key: 'all', label: 'Details', kind: 'fields' }],
        access: { canView: true, canAdd: true, canEdit: true, canDelete: true },
      }),
    [],
  );
  return <CrudPage schema={schema} />;
}
